import config from '../../config/config';
import { sessionAwareFetch } from './sessionAwareFetch';

let providerHighlightsRequest = null;

async function parseBusinessApiError(response) {
  let body = {};
  try {
    body = await response.json();
  } catch {
    /* non-JSON body */
  }
  const err = new Error(body.error || `HTTP error! status: ${response.status}`);
  err.status = response.status;
  if (body.code) err.code = body.code;
  return err;
}

export const businessService = {
  /** Admin-only: all businesses including unpublished (requires admin_user session). */
  async getAllBusinessesForAdmin() {
    const url = new URL(`${config.apimUrl}/business-api/`);
    url.searchParams.set('scope', 'admin');

    const response = await sessionAwareFetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': config.apimKeyBusinessService,
      },
    });

    if (!response.ok) {
      throw await parseBusinessApiError(response);
    }

    return await response.json();
  },

  async getAllBusinesses(includeServices = false) {
    const url = new URL(`${config.apimUrl}/business-api/`);

    // Append query param
    url.searchParams.set("includeServices", String(includeServices));

    const response = await sessionAwareFetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': config.apimKeyBusinessService,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch businesses');
    }

    return await response.json();
  },

  /** Published providers for the site ticker (name, city, trade slugs). Cached for the session. */
  async getProviderHighlights() {
    if (!providerHighlightsRequest) {
      providerHighlightsRequest = sessionAwareFetch(
        `${config.apimUrl}/business-api/highlights`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Ocp-Apim-Subscription-Key': config.apimKeyBusinessService,
          },
        },
      )
        .then(async (response) => {
          if (!response.ok) {
            throw new Error('Failed to fetch provider highlights');
          }
          return response.json();
        })
        .catch((error) => {
          providerHighlightsRequest = null;
          throw error;
        });
    }

    return providerHighlightsRequest;
  },

  /*
  async getAllBusinessesWithServices() {
    const response = await sessionAwareFetch(`${config.apimUrl}/business-api/Services`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': config.apimKeyBusinessService,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch businesses');
    }

    return await response.json();
  },
*/

  async getBusiness(identifier, includeServices = false, scope) {
    if (!identifier) {
      throw new Error('Must provide a business slug or ID');
    }

    const url = new URL(`${config.apimUrl}/business-api/${identifier}`);
    url.searchParams.set("includeServices", String(includeServices));
    if (scope) {
      url.searchParams.set("scope", scope);
    }

    const response = await sessionAwareFetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': config.apimKeyBusinessService,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  },

  /**
   * Provider dashboards (business profile + service dashboard): one GET that returns the
   * business with Services embedded (`includeServices=true`, owner scope).
   */
  async getBusinessForOwnerDashboard(businessId) {
    return businessService.getBusiness(businessId, true, 'owner');
  },

  /*
    async getBusinessByIdWithServices(businessId) {
      const response = await sessionAwareFetch(
        `${config.apimUrl}/business-api/${businessId}/Services`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Ocp-Apim-Subscription-Key': config.apimKeyBusinessService,
          },
        },
      );
  
      if (!response.ok) {
        throw new Error('Failed to fetch business');
      }
  
      return await response.json();
    },
  */
  async createBusiness(business) {
    const response = await sessionAwareFetch(`${config.apimUrl}/business-api/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': config.apimKeyBusinessService,
      },
      credentials: 'include',
      body: JSON.stringify(business),
    });

    if (!response.ok) {
      throw new Error('Failed to create business');
    }

    return await response.json();
  },

  /**
   * Public provider signup: creates business + business_user account and sets auth cookie (same as Login).
   * @param {{ business: object, account: object }} payload
   */
  async completeProviderEnrollment(payload) {
    const response = await sessionAwareFetch(`${config.apimUrl}/business-api/enroll`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': config.apimKeyBusinessService,
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw await parseBusinessApiError(response);
    }

    return await response.json();
  },

  /**
   * Logged-in provider enroll: POST /enroll with `{ business }` only; links business to session account.
   */
  async completeAuthenticatedProviderEnrollment({ business }) {
    const response = await sessionAwareFetch(`${config.apimUrl}/business-api/enroll`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': config.apimKeyBusinessService,
      },
      credentials: 'include',
      body: JSON.stringify({ business }),
    });

    if (!response.ok) {
      throw await parseBusinessApiError(response);
    }

    return await response.json();
  },

  /**
   * Consumer-only signup after POST /enroll returned 409 with code business_already_exists.
   * POST /enroll?consumer-fallback=true with same body as completeProviderEnrollment. Session cookie is set like the default enroll path.
   */
  async completeProviderConsumerFallback(payload) {
    const url = new URL(`${config.apimUrl}/business-api/enroll`);
    url.searchParams.set('consumer-fallback', 'true');
    const response = await sessionAwareFetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': config.apimKeyBusinessService,
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw await parseBusinessApiError(response);
    }

    return await response.json();
  },

  async deleteBusiness(businessId) {
    const response = await sessionAwareFetch(
      `${config.apimUrl}/business-api/?BusinessId=${businessId}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Ocp-Apim-Subscription-Key': config.apimKeyBusinessService,
        },
        credentials: 'include',
      },
    );

    if (!response.ok) {
      throw new Error('Failed to delete business');
    }

    if (response.status === 204) {
      return null;
    }

    const text = await response.text();
    return text ? JSON.parse(text) : null;
  },

  async editBusiness(businessId, editBusiness) {
    const response = await sessionAwareFetch(
      `${config.apimUrl}/business-api/?BusinessId=${businessId}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Ocp-Apim-Subscription-Key': config.apimKeyBusinessService,
        },
        credentials: 'include',
        body: JSON.stringify(editBusiness),
      },
    );

    if (!response.ok) {
      throw await parseBusinessApiError(response);
    }

    return await response.json();
  },

  async uploadBusinessImage(image, fileName) {
    const response = await sessionAwareFetch(
      `${config.apimUrl}/business-api/My-Business/Photo-Token-Url?fileName=${fileName}`,
      {
        method: 'GET',
        headers: {
          'Ocp-Apim-Subscription-Key': config.apimKeyBusinessService,
        },
        credentials: 'include',
      },
    );

    if (!response.ok) {
      throw new Error('Failed to generate sas token url');
    }

    const data = await response.json();
    const uploadUrl = data.uploadUrl;
    const fileUrl = data.fileUrl;

    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: image,
      headers: {
        'Content-Type': 'application/octet-stream',
        'x-ms-blob-type': 'BlockBlob',
      },
    });

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload business image');
    }

    return fileUrl;
  },

  async recomputeNkyCps() {
    const url = `${config.apimUrl}/business-api/nky-cps/recompute`;

    const response = await sessionAwareFetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Ocp-Apim-Subscription-Key': config.apimKeyBusinessService,
      },
    });

    if (!response.ok) {
      throw await parseBusinessApiError(response);
    }

    return await response.json();
  },
};
